import { useCallback, useEffect, useRef, useState } from 'react';
import { differenceInDays } from 'date-fns';
import { PremiumFeatureAccessLevel, RoleType } from '@sdk/authentication/types';
import useSlingApi from '@sdk/api/useSlingApi';
import { IntegrationPlatform } from '@sdk/integrations/types';
import { WithFeatureFlag } from '@sdk/featureFlags/WithFeatureFlag';
import { FeatureFlag } from '@sdk/featureFlags/types';
import useToast from '@sdk/common/toast/useToast';
import Tooltip, { hideTooltip } from '@sdk/common/tooltip/Tooltip';
import useAnalytics from '@sdk/analytics/useAnalytics';
import { AnalyticsEvents } from '@sdk/analytics/types';
import useAuth from '@sdk/authentication/useAuth';
import { ShouldShowElement } from '@sdk/authentication/types/apiTypes';
import { SUBSCRIPTION_LEVELS } from 'sling-sdk/src/account/constants';

import { useShouldShow } from '@web/common/hooks/useShouldShow';
import { usePreviousValue } from '@web/common/hooks/usePreviousValue';
import { useShiftsBridge } from '@web/shifts/ShiftsBridgeProvider';
import { FeatureNames } from '@web/account/types';
import { PremiumModalNames } from '@web/premiumGuard/constants/modalNames';
import { ModalGuard } from '@web/premiumGuard/ModalGuard/ModalGuard';

import { ProjectedSalesImportModal } from './Modal';
import { ProjectedSalesImportEducationalModal } from './EducationalModal';
import { useProjectedSalesTooltip } from './useProjectedSalesTooltip';
import { ButtonWrapper, ModalWrapper, ButtonIcon } from './styles';

export type Props = {
  days: string[];
  locationIds: number[];
  totalProjectedSales: number;
  importedAt: Date;
};

export const ProjectedSalesImportButton = ({
  days,
  locationIds,
  totalProjectedSales,
  importedAt,
}: Props) => {
  const flag = totalProjectedSales
    ? 'projectedSalesImportConfirmationOverride'
    : 'projectedSalesImportConfirmation';
  const { currentUser } = useAuth();
  const { track } = useAnalytics();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { integrations } = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();
  const { shouldShow } = useShouldShow(flag, currentUser);
  const [dateFrom, dateTo] = [days[0], days[days.length - 1]];
  const previousDays = usePreviousValue(days);
  const tooltipRef = useRef<HTMLButtonElement>();
  const shiftsBridge = useShiftsBridge();
  const tooltipValidators = {
    projectedSalesImportOldDataTooltip: useCallback(
      () => !isModalVisible && importedAt && differenceInDays(new Date(), importedAt) > 14,
      [importedAt, isModalVisible]
    ),
  };
  const subscriptionLevel = currentUser.org.subscriptionLevel;
  const { shouldShow: shouldShowOldDataTooltip } = useShouldShow(
    'projectedSalesImportOldDataTooltip',
    currentUser,
    { validate: tooltipValidators.projectedSalesImportOldDataTooltip }
  );
  const getTooltipFlag = useCallback(
    () =>
      shouldShowOldDataTooltip()
        ? 'projectedSalesImportOldDataTooltip'
        : 'projectedSalesImportInitialTooltip',
    [shouldShowOldDataTooltip]
  );
  const [tooltipFlag, setTooltipFlag] = useState<ShouldShowElement>(getTooltipFlag());
  const refreshTooltipFlag = useCallback(() => setTooltipFlag(getTooltipFlag()), [getTooltipFlag]);
  const { getTooltipContent, showTooltip, tooltipPlace, tooltipWidth } = useProjectedSalesTooltip(
    tooltipFlag,
    tooltipRef,
    new Date(dateFrom),
    importedAt,
    refreshTooltipFlag,
    tooltipValidators,
    subscriptionLevel
  );
  const isFreeCustomer = subscriptionLevel === SUBSCRIPTION_LEVELS.FREE;
  const handleSuccess = useCallback(
    (e) => {
      showSuccessToast('Projected sales were synced for this week', {
        style: { whiteSpace: 'nowrap' },
      });
      setIsModalVisible(false);

      shiftsBridge.onProjectedSalesImport();
    },
    [shiftsBridge, showSuccessToast]
  );
  const handleError = useCallback(
    (e) => {
      showErrorToast(e?.data?.message);
      setIsModalVisible(false);
    },
    [showErrorToast]
  );
  const handleConfirm = useCallback(() => {
    track(AnalyticsEvents.projectedSalesImportButtonClicked, `${dateFrom}/${dateTo}`);
    integrations
      .importProjectedSales(IntegrationPlatform.toast, dateFrom, dateTo, locationIds)
      .then(handleSuccess)
      .catch(handleError);
  }, [dateFrom, dateTo, locationIds, integrations, handleError, handleSuccess, track]);
  const handleClick = useCallback(() => {
    if (shouldShow()) {
      setIsModalVisible(true);
      hideTooltip(tooltipRef.current);
    } else {
      handleConfirm();
    }
  }, [handleConfirm, shouldShow, tooltipRef]);
  const handleCancel = useCallback(() => setIsModalVisible(false), []);

  useEffect(() => {
    hideTooltip(tooltipRef.current);
    refreshTooltipFlag();
  }, [days, previousDays, shouldShowOldDataTooltip, refreshTooltipFlag, tooltipRef]);

  if (![RoleType.manager, RoleType.admin].includes(currentUser.role)) {
    return null;
  }

  if (!currentUser.org.hasToastIntegration) {
    return null;
  }

  return (
    <WithFeatureFlag flag={FeatureFlag.PROJECTED_SALES_IMPORT}>
      <>
        <ButtonWrapper
          showPremiumGuard={isFreeCustomer}
          data-for="projected-sales-import-tip"
          data-tip
          data-place={tooltipPlace}
          onClick={handleClick}
          ref={tooltipRef}>
          <ButtonIcon showPremiumGuard={isFreeCustomer} />
        </ButtonWrapper>
        <Tooltip
          id="projected-sales-import-tip"
          clickable={true}
          attachToBody
          getContent={getTooltipContent}
          minWidth={tooltipWidth}
        />

        {isFreeCustomer ? (
          <ModalGuard
            premiumFeatureAccessLevel={SUBSCRIPTION_LEVELS.PREMIUM as PremiumFeatureAccessLevel}
            premiumFeatureName={FeatureNames.projectedSalesSync}
            premiumModalName={PremiumModalNames.projectedSalesSync}
            onDismiss={handleCancel}
            isVisible={isModalVisible}
          />
        ) : (
          <ModalWrapper>
            <ProjectedSalesImportModal
              about={flag}
              isVisible={isModalVisible}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
            />
          </ModalWrapper>
        )}

        <ProjectedSalesImportEducationalModal onButtonClick={showTooltip} />
      </>
    </WithFeatureFlag>
  );
};
